import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutData, faqData } from '@/mocks/about';
import { OrganizationSchema, FAQPageSchema, BreadcrumbListSchema, WebPageSchema } from '@/components/base/SEO';

export default function AboutPage() {
  const [activeFaq, setActiveFaq] = useState<number | null>(null);

  const pageTitle = '关于我们 - 品牌介绍与联系方式 | 51网址导航';
  const pageDesc = '51网址导航成立于2018年，是国内领先的网址导航服务平台。收录百万网址，九大核心分类。了解51网址导航的品牌故事、口碑评价与联系方式。';
  const canonicalUrl = 'http://chiguadao.cn/about';

  useEffect(() => {
    document.title = pageTitle;
    document.querySelector('meta[name="description"]')?.setAttribute('content', pageDesc);
    document.querySelector('link[rel="canonical"]')?.setAttribute('href', canonicalUrl);

    const existingScript = document.getElementById('schema-jsonld');
    if (existingScript) existingScript.remove();

    const schemas = [
      OrganizationSchema(),
      WebPageSchema('关于我们 - 51网址导航', canonicalUrl, pageDesc),
      BreadcrumbListSchema([
        { name: '首页', url: 'http://chiguadao.cn/' },
        { name: '关于我们', url: canonicalUrl },
      ]),
      FAQPageSchema(faqData),
    ];

    const jsonLd = JSON.stringify({ '@context': 'https://schema.org', '@graph': schemas }, null, 2);
    const script = document.createElement('script');
    script.id = 'schema-jsonld';
    script.type = 'application/ld+json';
    script.textContent = jsonLd;
    document.head.appendChild(script);
  }, []);

  return (
    <>
      <Navbar />
      <main className="pt-20 min-h-screen">
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { name: '首页', path: '/' },
                { name: '关于我们' },
              ]}
            />
            <div className="py-12 md:py-16 text-center">
              <h1 className="font-heading font-bold text-4xl md:text-5xl text-foreground-900">
                关于{aboutData.brandName}
              </h1>
              <p className="mt-4 text-foreground-500 text-lg max-w-2xl mx-auto">
                {aboutData.slogan}
              </p>
            </div>
          </div>
        </div>

        <section className="py-14 md:py-20">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="mb-14">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-900 mb-6">品牌介绍</h2>
              <p className="text-foreground-700 text-base leading-relaxed">{aboutData.introduction}</p>
              <p className="mt-4 text-foreground-700 text-base leading-relaxed">{aboutData.mission}</p>
            </div>

            <div className="mb-14">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-900 mb-8">发展历程</h2>
              <div className="relative">
                <div className="absolute left-5 top-0 bottom-0 w-px bg-foreground-200"></div>
                <div className="space-y-8">
                  {aboutData.story.map((item) => (
                    <div key={item.year} className="relative pl-14">
                      <div className="absolute left-3.5 top-1 w-3.5 h-3.5 rounded-full bg-accent-500 border-2 border-background-50"></div>
                      <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
                        <span className="text-xs text-accent-600 font-label font-bold">{item.year}</span>
                        <h3 className="font-heading font-bold text-foreground-900 mt-1 mb-2">{item.title}</h3>
                        <p className="text-foreground-600 text-sm leading-relaxed">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mb-14">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-900 mb-8">品牌数据</h2>
              <div className="grid grid-cols-3 gap-0 divide-x divide-foreground-200 bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
                {aboutData.stats.map((stat) => (
                  <div key={stat.label} className="p-6 md:p-8 text-center">
                    <p className="font-label font-bold text-3xl md:text-4xl text-primary-500">{stat.value}</p>
                    <p className="mt-2 text-foreground-500 text-sm">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-900 mb-8 text-center">用户口碑</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {aboutData.testimonials.map((t, i) => (
                <div key={i} className="bg-background-50 border border-background-200/70 rounded-xl p-6">
                  <p className="text-foreground-700 text-sm leading-relaxed italic">
                    "{t.content}"
                  </p>
                  <div className="mt-4 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-accent-100 flex items-center justify-center">
                      <span className="font-heading font-bold text-accent-700 text-sm">{t.name[0]}</span>
                    </div>
                    <div>
                      <p className="font-label font-semibold text-sm text-foreground-900">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-900 mb-8">联系我们</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-primary-500"></i>
                  </div>
                  <div>
                    <p className="font-label font-semibold text-sm text-foreground-900">电子邮箱</p>
                    <p className="text-foreground-600 text-sm mt-1">{aboutData.contact.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-accent-50 flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-accent-500"></i>
                  </div>
                  <div>
                    <p className="font-label font-semibold text-sm text-foreground-900">客服电话</p>
                    <p className="text-foreground-600 text-sm mt-1">{aboutData.contact.phone}</p>
                    <p className="text-foreground-400 text-xs mt-0.5">{aboutData.contact.workingHours}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-secondary-50 flex items-center justify-center flex-shrink-0">
                    <i className="ri-wechat-line text-secondary-500"></i>
                  </div>
                  <div>
                    <p className="font-label font-semibold text-sm text-foreground-900">微信</p>
                    <p className="text-foreground-600 text-sm mt-1">{aboutData.contact.wechat}</p>
                  </div>
                </div>
              </div>
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-secondary-50 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-secondary-500"></i>
                  </div>
                  <div>
                    <p className="font-label font-semibold text-sm text-foreground-900">公司地址</p>
                    <p className="text-foreground-600 text-sm mt-1 leading-relaxed">{aboutData.contact.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
                    <i className="ri-weibo-line text-primary-500"></i>
                  </div>
                  <div>
                    <p className="font-label font-semibold text-sm text-foreground-900">官方微博</p>
                    <p className="text-foreground-600 text-sm mt-1">{aboutData.contact.weibo}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-900 mb-8 text-center">常见问题</h2>
            <div className="space-y-3">
              {faqData.map((faq, i) => (
                <div key={i} className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setActiveFaq(activeFaq === i ? null : i)}
                    className="w-full flex items-center justify-between p-5 text-left cursor-pointer hover:bg-background-50 transition-colors"
                  >
                    <span className="font-label font-semibold text-sm text-foreground-900 pr-4">{faq.question}</span>
                    <i
                      className={`ri-arrow-down-s-line text-foreground-500 transition-transform duration-200 flex-shrink-0 ${
                        activeFaq === i ? 'rotate-180' : ''
                      }`}
                    ></i>
                  </button>
                  {activeFaq === i && (
                    <div className="px-5 pb-5">
                      <p className="text-foreground-600 text-sm leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}